/**
 * "Request Documents" (client mockups 2026-08-27) — the landlord asks
 * selected household members for specific documents, two steps:
 *   1. applicant picker — selectable member cards with a role chip;
 *   2. document picker — suggested checkboxes + free-text "add a document",
 *      then Send Email / Send Message.
 * Both send buttons post `{ intent: 'requestDocuments', applicationIds,
 * documents, channel }` — the server records the request on each member's
 * review step, flips the application back to in_progress so the applicant can
 * upload and RESUBMIT (mirrors Request Bank Account), and emails when the
 * channel is email. Send Message additionally opens the device SMS composer
 * (Reach Out pattern) prefilled for the selected members with a phone.
 */
import { ArrowRight, Check, Mail, Plus, Send, X } from 'lucide-react'
import { useState } from 'react'
import { useFetcher } from 'react-router'
import type {
	ApplicationSummary,
	DetailActionResult,
	GroupMember,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import {
	ApplicantPicker,
	CheckCircle,
	buildApplicantOptions,
} from './ApplicantPicker'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
} from './dialog-bits'

/** Suggested request list (mockup); free-text rows extend it. */
const SUGGESTED_DOCUMENTS = [
	'Pay Stubs',
	'Employment Letter',
	'Landlord Reference',
	'Pet Vaccination',
]

export function RequestDocumentsDialog({
	open,
	onOpenChange,
	application,
	groupMembers,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	groupMembers: GroupMember[]
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const [step, setStep] = useState<1 | 2>(1)
	const [selectedIds, setSelectedIds] = useState<string[]>([application.id])
	const [selectedDocs, setSelectedDocs] = useState<string[]>([])
	const [customDocs, setCustomDocs] = useState<string[]>([])
	const [customDraft, setCustomDraft] = useState('')
	const busy = fetcher.state !== 'idle'

	const handleOpenChange = (next: boolean) => {
		if (!next) {
			setStep(1)
			setSelectedIds([application.id])
			setSelectedDocs([])
			setCustomDocs([])
			setCustomDraft('')
		}
		onOpenChange(next)
	}

	const options = buildApplicantOptions(application, groupMembers)

	const toggleMember = (id: string) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		)
	}
	const toggleDoc = (name: string) => {
		setSelectedDocs((prev) =>
			prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
		)
	}
	const addCustom = () => {
		const name = customDraft.trim()
		if (!name) return
		const exists = [...SUGGESTED_DOCUMENTS, ...customDocs].some(
			(doc) => doc.toLowerCase() === name.toLowerCase(),
		)
		if (!exists) setCustomDocs((prev) => [...prev, name])
		setSelectedDocs((prev) =>
			prev.some((doc) => doc.toLowerCase() === name.toLowerCase())
				? prev
				: [...prev, name],
		)
		setCustomDraft('')
	}

	const chosenDocs = selectedDocs
	const selectedPhones = options
		.filter((option) => selectedIds.includes(option.id) && option.phone)
		.map((option) => option.phone as string)

	const submit = (channel: 'email' | 'message') => {
		fetcher.submit(
			{
				intent: 'requestDocuments',
				applicationIds: selectedIds.join(','),
				documents: JSON.stringify(chosenDocs),
				channel,
			},
			{ method: 'post' },
		)
		if (channel === 'message' && selectedPhones.length > 0) {
			// Reach Out pattern: the device SMS composer; ?&body= works across
			// iOS/Android. Multiple recipients ride a comma list (best effort).
			const body = encodeURIComponent(
				`Hi, we need the following documents for your rental application: ${chosenDocs.join(
					', ',
				)}. Please open your application to upload them.`,
			)
			window.location.href = `sms:${selectedPhones.join(',')}?&body=${body}`
		}
	}

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Document request sent"
				onSuccess={() => handleOpenChange(false)}
			/>
			<DetailDialog
				open={open}
				onOpenChange={handleOpenChange}
				title="Request Documents"
				description={
					step === 1
						? 'Select which applicants you would like to request additional documents.'
						: 'What do you need?'
				}
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-2xl"
			>
				{step === 1 ? (
					<>
						<ApplicantPicker
							className="mt-4"
							options={options}
							selectedIds={selectedIds}
							onToggle={toggleMember}
						/>
						<div className="mt-6 flex justify-end">
							<PillButton
								tone="dark"
								disabled={selectedIds.length === 0}
								onClick={() => setStep(2)}
							>
								Next <ArrowRight className="size-4" />
							</PillButton>
						</div>
					</>
				) : (
					<>
						<div className="mt-4 flex flex-wrap gap-3">
							{[...SUGGESTED_DOCUMENTS, ...customDocs].map((name) => {
								const checked = chosenDocs.some(
									(doc) => doc.toLowerCase() === name.toLowerCase(),
								)
								return (
									<button
										key={name}
										type="button"
										onClick={() => toggleDoc(name)}
										className={cn(
											'flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3.5 transition-colors',
											checked
												? 'border-[#32302F]'
												: 'border-[#F0F0F0] hover:border-[#D6D3D1]',
										)}
									>
										<CheckCircle checked={checked} />
										<span className="text-[15px] text-[#32302F]">{name}</span>
									</button>
								)
							})}
						</div>
						<div className="mt-4 flex items-center gap-2 rounded-2xl border border-[#F0F0F0] px-4 py-1.5 focus-within:border-[#D6D3D1]">
							<Plus className="size-4 shrink-0 text-[#747474]" />
							<input
								value={customDraft}
								onChange={(event) => setCustomDraft(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										event.preventDefault()
										addCustom()
									}
								}}
								onBlur={addCustom}
								placeholder="Add A Document You Need"
								className="h-10 w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#B3B0AD]"
							/>
						</div>
						<InlineActionError fetcher={fetcher} open={open} />
						<div className="mt-6 flex flex-wrap items-center justify-between gap-3">
							<PillButton
								tone="outline"
								disabled={busy}
								onClick={() => setStep(1)}
							>
								Back
							</PillButton>
							<div className="flex items-center gap-3">
								<PillButton
									tone="outline"
									disabled={busy || chosenDocs.length === 0}
									onClick={() => submit('email')}
								>
									Send Email <Mail className="size-4" />
								</PillButton>
								<PillButton
									tone="dark"
									disabled={
										busy ||
										chosenDocs.length === 0 ||
										selectedPhones.length === 0
									}
									title={
										selectedPhones.length === 0
											? 'No phone number on file for the selected applicants'
											: undefined
									}
									onClick={() => submit('message')}
								>
									Send Message <Send className="size-4" />
								</PillButton>
							</div>
						</div>
					</>
				)}
			</DetailDialog>
		</>
	)
}
